import { FC, Suspense, useEffect, useRef, useState } from 'react';
import ToastExportXLSorCSV from '../ExportCSV/ToastExportXLSorCSV';
import { Toast } from 'primereact/toast';
import ToastSuccessful from '../ExportCSV/ToastSuccessful';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedReportHubFilter } from '../../redux/reducers/filterReportHubSlice';
import {
  getDetailedSkillReportByStandardColumns,
  getDetailedSkillReportByStandardRows,
  getDetailedSkillReportByUnitsColumns,
  getDetailedSkillReportByUnitsRows,
} from '../../graphQLOperations/ReportHub';
import { useLazyLoadQuery } from 'react-relay';
import { ReportGroupBy, ReportOrderBy } from '../../models/Report';
import { createAndExportXLS } from '../../businessLogicService/createAndExportGenericXLS';
import { showDownloadToast, showSuccessfullToast } from '../../businessLogicService/exportToastHandler';
import { setCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { setReportHubQuerysInProgress } from '../../redux/reducers/reportHubQuerysInProgress';
import { getDateParsedForReportTitle } from '../../businessLogicService/dataParsedForReportTitle';
import { ReportHubDetailedSkillReportByUnitsRowsQuery } from '../../graphQLOperations/__generated__/ReportHubDetailedSkillReportByUnitsRowsQuery.graphql';
import { ReportHubDetailedSkillReportByStandardsRowsQuery } from '../../graphQLOperations/__generated__/ReportHubDetailedSkillReportByStandardsRowsQuery.graphql';
import { ReportHubDetailedSkillReportByUnitsColumnsQuery } from '../../graphQLOperations/__generated__/ReportHubDetailedSkillReportByUnitsColumnsQuery.graphql';
import { ReportHubDetailedSkillReportByStandardsColumnsQuery } from '../../graphQLOperations/__generated__/ReportHubDetailedSkillReportByStandardsColumnsQuery.graphql';

interface DetailedSkillReportQueryProps {
  setFetchingData: (value: boolean) => void;
  setData: (value: ArrayBuffer | null) => void;
}

export const DownloadXlsDetailedSkillReport: FC = () => {
  const dispatch = useAppDispatch();
  const reportHubFilterRedux = useAppSelector(selectedReportHubFilter);

  const [fetchingData, setFetchingData] = useState(false);
  const [data, setData] = useState<ArrayBuffer | null>(null);

  const downloadToastRef = useRef<Toast | null>(null);
  const successfullToastRef = useRef<Toast | null>(null);

  const clear = async () => {
    setTimeout(() => {
      downloadToastRef?.current?.clear();
    }, 500);
  };

  useEffect(() => {
    if (reportHubFilterRedux.key) {
      showDownloadToast(downloadToastRef);
      dispatch(setCsvProcess(true));
      setFetchingData(true);
    }
  }, [reportHubFilterRedux.key]);

  useEffect(() => {
    if (data && !fetchingData) {
      createAndExportXLS(new Uint8Array(data), `Progress_${getDateParsedForReportTitle()}.xlsx`)
        .then(() => {
          clear().then(() => {
            showSuccessfullToast(successfullToastRef);
            setTimeout(() => {
              dispatch(setCsvProcess(false));
              dispatch(setReportHubQuerysInProgress(false));
            }, 3000);
          });
        })
        .catch(() => {
          dispatch(setCsvProcess(false));
          dispatch(setReportHubQuerysInProgress(false));
        });
    }
  }, [data]);

  return (
    <>
      {fetchingData && (
        <Suspense>
          <DownloadXlsDetailedSkillReportQuery setFetchingData={setFetchingData} setData={setData} />
        </Suspense>
      )}
      <ToastExportXLSorCSV downloadToastRef={downloadToastRef} clear={clear} progressBarType="indeterminate" />
      <ToastSuccessful successfullToastRef={successfullToastRef} />
    </>
  );
};

export const DownloadXlsDetailedSkillReportQuery = ({ setFetchingData, setData }: DetailedSkillReportQueryProps) => {
  const reportHubFilterRedux = useAppSelector(selectedReportHubFilter);

  const dataByUnitsRows = useLazyLoadQuery<ReportHubDetailedSkillReportByUnitsRowsQuery>(
    getDetailedSkillReportByUnitsRows,
    {
      classIds: reportHubFilterRedux.values.classes,
      targetGroupId: reportHubFilterRedux.values.roadmap,
      standardStateId: reportHubFilterRedux.values.standards,
      skip:
        reportHubFilterRedux.values.groupedBy === ReportGroupBy.STANDARD ||
        reportHubFilterRedux.values.orderBy === ReportOrderBy.COLUMNS,
    },
    { fetchPolicy: 'network-only' },
  );

  const dataByStandardsRows = useLazyLoadQuery<ReportHubDetailedSkillReportByStandardsRowsQuery>(
    getDetailedSkillReportByStandardRows,
    {
      classIds: reportHubFilterRedux.values.classes,
      targetGroupId: reportHubFilterRedux.values.roadmap,
      standardStateId: reportHubFilterRedux.values.standards,
      skip:
        reportHubFilterRedux.values.groupedBy === ReportGroupBy.UNIT ||
        reportHubFilterRedux.values.orderBy === ReportOrderBy.COLUMNS,
    },
    { fetchPolicy: 'network-only' },
  );

  const dataByUnitsColumns = useLazyLoadQuery<ReportHubDetailedSkillReportByUnitsColumnsQuery>(
    getDetailedSkillReportByUnitsColumns,
    {
      classIds: reportHubFilterRedux.values.classes,
      targetGroupId: reportHubFilterRedux.values.roadmap,
      standardStateId: reportHubFilterRedux.values.standards,
      skip:
        reportHubFilterRedux.values.groupedBy === ReportGroupBy.STANDARD ||
        reportHubFilterRedux.values.orderBy === ReportOrderBy.ROWS,
    },
  );

  const dataByStandardsColumns = useLazyLoadQuery<ReportHubDetailedSkillReportByStandardsColumnsQuery>(
    getDetailedSkillReportByStandardColumns,
    {
      classIds: reportHubFilterRedux.values.classes,
      targetGroupId: reportHubFilterRedux.values.roadmap,
      standardStateId: reportHubFilterRedux.values.standards,
      skip:
        reportHubFilterRedux.values.groupedBy === ReportGroupBy.UNIT ||
        reportHubFilterRedux.values.orderBy === ReportOrderBy.ROWS,
    },
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (
      reportHubFilterRedux.values.groupedBy === ReportGroupBy.UNIT &&
      reportHubFilterRedux.values.orderBy === ReportOrderBy.ROWS &&
      dataByUnitsRows
    ) {
      setData(
        dataByUnitsRows.transposedSkillProgressReportGroupedByUnitsInBytes?.data
          ? new Uint8Array(dataByUnitsRows.transposedSkillProgressReportGroupedByUnitsInBytes.data).buffer
          : null,
      );
      setFetchingData(false);
    }
  }, [dataByUnitsRows]);

  useEffect(() => {
    if (
      reportHubFilterRedux.values.groupedBy === ReportGroupBy.STANDARD &&
      reportHubFilterRedux.values.orderBy === ReportOrderBy.ROWS &&
      dataByStandardsRows
    ) {
      setData(
        dataByStandardsRows.transposedSkillProgressReportGroupedByStandardsInBytes?.data
          ? new Uint8Array(dataByStandardsRows.transposedSkillProgressReportGroupedByStandardsInBytes.data).buffer
          : null,
      );
      setFetchingData(false);
    }
  }, [dataByStandardsRows]);

  useEffect(() => {
    if (
      reportHubFilterRedux.values.groupedBy === ReportGroupBy.UNIT &&
      reportHubFilterRedux.values.orderBy === ReportOrderBy.COLUMNS &&
      dataByUnitsColumns
    ) {
      setData(
        dataByUnitsColumns.skillProgressReportGroupedByUnitsInBytes?.data
          ? new Uint8Array(dataByUnitsColumns.skillProgressReportGroupedByUnitsInBytes.data).buffer
          : null,
      );
      setFetchingData(false);
    }
  }, [dataByUnitsColumns]);

  useEffect(() => {
    if (
      reportHubFilterRedux.values.groupedBy === ReportGroupBy.STANDARD &&
      reportHubFilterRedux.values.orderBy === ReportOrderBy.COLUMNS &&
      dataByStandardsColumns
    ) {
      setData(
        dataByStandardsColumns.skillProgressReportGroupedByStandardsInBytes?.data
          ? new Uint8Array(dataByStandardsColumns.skillProgressReportGroupedByStandardsInBytes.data).buffer
          : null,
      );
      setFetchingData(false);
    }
  }, [dataByStandardsColumns]);

  return <div style={{ display: 'none' }}></div>;
};
